import type { UseQueryOptions } from "@tanstack/react-query";
import { useQueries } from "@tanstack/react-query";
import { Link } from "react-router";
import { twJoin } from "tailwind-merge";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { Badge } from "@/design-system/Badge";
import { cardSurfaceVariants } from "@/design-system/Card";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { Stack } from "@/design-system/Stack";
import { Tiles } from "@/design-system/Tiles";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { useProjectAppBrandingConfig } from "@/providers/ProjectAppBrandingConfigProvider";
import { useIntegrationQueries } from "@/queries/integrations/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

type IntegrationGroup = "general" | "tickets" | "bookable-assets" | "services" | "events" | "users" | "other";

type IntegrationConfig<TData> = {
  name: string;
  category?: IntegrationGroup;
  query: UseQueryOptions<any, any, TData, any>;
  permission: boolean;
  route: string;
  getEnabled: (data: TData | undefined) => boolean;
};

/**
 * One ordered integration group. Each integration route is a project-relative destination, and `enabled` reflects
 * the current project configuration rather than availability.
 */
export type ProjectIntegrationGroupData = {
  name: IntegrationGroup;
  label: string;
  integrations: Array<{ to: string; name: string; enabled: boolean }>;
};

const integration = <TData,>(config: IntegrationConfig<TData>) => config;

/** Renders the project integration overview using live configuration queries. */
export function IntegrationsPage(): React.ReactNode {
  const { t } = useTranslation();
  const hasPermission = usePermission();
  const sessionUser = useSessionUser();
  const slug = useSlug();
  const { appBrandingConfig } = useProjectAppBrandingConfig();
  const integrationQueries = useIntegrationQueries();

  const integrations = [
    integration({
      name: t("page.integration.rems.title"),
      category: "general",
      query: integrationQueries.remsConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageRems),
      route: routes.projects.integrationRems({ slug }),
      getEnabled: (data) => Boolean(data?.tickets?.enabled || data?.contracts?.enabled),
    }),
    integration({
      name: t("page.integration.wunderbricks.title"),
      category: "general",
      query: integrationQueries.wunderbricksConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageWunderbricks),
      route: routes.projects.integrationWunderbricks({ slug }),
      getEnabled: (data) => Boolean(data?.contractsEnabled || data?.ticketsEnabled),
    }),
    integration({
      name: t("page.integration.health-district.title"),
      category: "general",
      query: integrationQueries.healthDistrictConfiguration(),
      permission:
        appBrandingConfig.id === "healthDistrict" && hasPermission((x) => x.integrations.canManageHealthDistrict),
      route: routes.projects.integrationHealthDistrict({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.ziezodan.title"),
      category: "tickets",
      query: integrationQueries.ziezodanConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageZiezodan),
      route: routes.projects.integrationZiezodan({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.homedna.title"),
      category: "tickets",
      query: integrationQueries.homeDnaConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageHomeDna),
      route: routes.projects.integrationHomeDna({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.dico.title"),
      category: "tickets",
      query: integrationQueries.dicoConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageDico),
      route: routes.projects.integrationDico({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.jansen-huybregts.title"),
      category: "tickets",
      query: integrationQueries.jansenHuybregtsConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageJansenHuybregts),
      route: routes.projects.integrationJansenHuybregts({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.office-rnd.title"),
      category: "users",
      query: integrationQueries.officeRndConfiguration(),
      permission:
        hasPermission((x) => x.integrations.canManageOfficeRnd) &&
        hasPermission((x) => x.addressManagement.canListAddresses),
      route: routes.projects.integrationOfficeRnd({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.hubspot.title"),
      category: "users",
      query: integrationQueries.hubSpotConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageHubSpot),
      route: routes.projects.integrationHubSpot({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.iqbi.title"),
      category: "services",
      query: integrationQueries.iqbiConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageIqbi),
      route: routes.projects.integrationIqbi({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.mypup.title"),
      category: "services",
      query: integrationQueries.myPupConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageMyPup),
      route: routes.projects.integrationMyPup({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.bringme.title"),
      category: "services",
      query: integrationQueries.bringmeConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageBringme),
      route: routes.projects.integrationBringme({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.outlook.title"),
      category: "bookable-assets",
      query: integrationQueries.outlookConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageOutlook),
      route: routes.projects.integrationOutlook({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.jim.title"),
      category: "bookable-assets",
      query: integrationQueries.jimConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageJim),
      route: routes.projects.integrationJim({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.swoogo-configurations.title"),
      category: "events",
      query: integrationQueries.swoogoConfigurations(),
      permission: hasPermission((x) => x.integrations.canManageSwoogo),
      route: routes.projects.integrationSwoogo({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.iloq.title"),
      category: "other",
      query: integrationQueries.iloqConfiguration(),
      permission: hasPermission((x) => x.integrations.canManageILoq),
      route: routes.projects.integrationILoq({ slug }),
      getEnabled: (data) => data?.enabled ?? false,
    }),
    integration({
      name: t("page.integration.copilot-configurations.title"),
      category: "other",
      query: integrationQueries.copilotConfigurations(),
      permission: hasPermission((x) => x.integrations.canManageCopilot),
      route: routes.projects.integrationCopilot({ slug }),
      getEnabled: (data) => (data?.configurations?.length ?? 0) > 0,
    }),
  ];

  const results = useQueries({ queries: integrations.map((def) => ({ ...def.query, enabled: def.permission })) });

  const error = results.find((r) => r.error)?.error ?? null;
  if (error) {
    return <ErrorPage error={error} />;
  }

  if (results.some((r) => r.isFetching)) {
    return <FullSizeLoader withPadding />;
  }

  const integrationGroupLabels = {
    general: t("page.integration.group.general"),
    tickets: t("page.integration.group.tickets"),
    users: t("page.integration.group.users"),
    "bookable-assets": t("page.integration.group.bookable-assets"),
    services: t("page.integration.group.services"),
    events: t("page.integration.group.events"),
    other: t("page.integration.group.other"),
  } as const satisfies Record<IntegrationGroup, string>;

  const integrationGroups = integrations.reduce((groups, integration, index) => {
    if (!integration.permission) {
      return groups;
    }

    const category = integration.category ?? "general";
    const group = groups.find((g) => g.name === category);
    const enabled = integration.getEnabled(results[index].data as any);

    if (group) {
      group.integrations.push({ to: integration.route, name: integration.name, enabled });
    } else {
      groups.push({
        name: category,
        label: integrationGroupLabels[category],
        integrations: [{ to: integration.route, name: integration.name, enabled }],
      });
    }

    return groups;
  }, [] as ProjectIntegrationGroupData[]);

  return <ProjectIntegrationsOverview projectName={sessionUser.project.name} integrationGroups={integrationGroups} />;
}

export function ProjectIntegrationsOverview({
  projectName,
  integrationGroups,
}: {
  projectName: string;
  integrationGroups: ProjectIntegrationGroupData[];
}): React.ReactNode {
  const { t } = useTranslation();

  return (
    <DocumentPage
      containerBoundary="inherit"
      tabTitle={t("page.integration.title")}
      breadcrumbs={<Breadcrumbs pages={[{ name: projectName }, { name: t("page.integration.title") }]} />}
    >
      <Stack gap="lg">
        {integrationGroups.map((group) => (
          <Stack key={group.name} gap="sm">
            <h2 className="text-heading-4">{group.label}</h2>
            <Tiles columns={{ base: 1, container: { sm: 2, lg: 3 } }} gap="sm">
              {group.integrations.map(({ to, name, enabled }) => (
                <Link
                  key={to}
                  to={to}
                  className={twJoin(
                    "flex flex-col gap-space-sm p-space-md text-text-primary no-underline",
                    cardSurfaceVariants({ interactive: true }),
                  )}
                >
                  <span className="text-body-emphasis">{name}</span>
                  <Badge variant={enabled ? "success" : "default"}>
                    {t(enabled ? "common.label.enabled" : "common.label.disabled")}
                  </Badge>
                </Link>
              ))}
            </Tiles>
          </Stack>
        ))}
      </Stack>
    </DocumentPage>
  );
}
